import React, { useCallback, useContext } from 'react';
import { EditModeContext } from '../edit-mode';
import { FEATURE_CROPPING } from '../flags';
import { selectItemForCrop } from '../state/actions/item-cropping';
import { useCanvasTransformationScale } from '../utils/canvas-transformation-context';
import { useWriteContext } from '../utils/use-context';
import { Crop } from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';

const BUTTON_HEIGHT = 32;
const BUTTON_OFFSET = 8;

interface CropButtonProps {
  itemId: string;
}

export const CropButton: React.FC<CropButtonProps> = ({ itemId }) => {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const scale = useCanvasTransformationScale();
  const { editMode } = useContext(EditModeContext);

  const scaledButtonHeight = BUTTON_HEIGHT / scale;
  const scaledOffset = BUTTON_OFFSET / scale;

  const handleCropClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setState({
        update: (state) => selectItemForCrop({ state, itemId }),
        commitToUndoStack: true,
      });
    },
    [setState, itemId],
  );

  if (!FEATURE_CROPPING) {
    return null;
  }

  const buttonStyle: React.CSSProperties = {
    position: 'absolute',
    bottom: scaledOffset,
    left: '50%',
    transform: 'translateX(-50%)',
    height: scaledButtonHeight,
    padding: `0 ${12 / scale}px`,
    fontSize: 14 / scale,
    lineHeight: `${scaledButtonHeight}px`,
    borderRadius: 6 / scale,
    backgroundColor: 'white',
    color: '#333',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4 / scale,
    boxShadow: `0 ${2 / scale}px ${8 / scale}px rgba(0, 0, 0, 0.15)`,
    whiteSpace: 'nowrap',
    zIndex: 10,
    // 부모 핸들 레이어가 pointerEvents none 이라 버튼만 auto 로 되살린다.
    // 단 draw/hand 모드는 outline div 게이팅과 동일하게 인터랙션 차단.
    pointerEvents:
      editMode === 'draw-solid' ||
      editMode === 'draw-blur' ||
      editMode === 'hand'
        ? 'none'
        : 'auto',
  };

  const iconStyle: React.CSSProperties = {
    width: 16 / scale,
    height: 16 / scale,
  };

  return (
    <button
      style={buttonStyle}
      onClick={handleCropClick}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <Crop style={iconStyle} />
      <span>{t('Editor.crop')}</span>
    </button>
  );
};
